import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type ConceptSeverity,
  type ConceptsRegistry,
  type Schema,
  schemasApi,
  type ValidationFinding,
  type ValidationGate,
} from '@/services/api/schemas'
import { errorMessage } from '@/utils/errors'

type FindingsBuckets = Record<ConceptSeverity, ValidationFinding[]>

function bucketFindings(findings: ValidationFinding[] | null): FindingsBuckets {
  const buckets: FindingsBuckets = { error: [], warning: [], hint: [], meta: [] }
  for (const f of findings ?? []) buckets[f.severity].push(f)
  return buckets
}

export interface UseSchemaValidationResult {
  registry: ConceptsRegistry | null
  conceptById: Record<string, ConceptsRegistry['concepts'][string]>
  findings: ValidationFinding[] | null
  findingsBySeverity: FindingsBuckets
  serverError: string | null
  validating: boolean
  validate: (schema: Record<string, unknown>, gate?: ValidationGate) => Promise<void>
  reset: () => void
}

/**
 * Shared schema-validation core. Loads the concept registry once and runs the
 * same validators the API uses against an already-parsed schema object,
 * returning findings (bucketed by severity), the registry, and server-error
 * state.
 *
 * Reused by the schema playground (which adds JSON-string parsing on top — see
 * `useSchemaPlayground`) and the schema detail page (which validates the loaded
 * schema directly), so the validate path lives in exactly one place.
 */
export function useSchemaValidation(): UseSchemaValidationResult {
  const [findings, setFindings] = useState<ValidationFinding[] | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)
  const [registry, setRegistry] = useState<ConceptsRegistry | null>(null)

  useEffect(() => {
    schemasApi
      .getConcepts()
      .then(setRegistry)
      .catch(err => {
        // Concepts panel is optional; failure shouldn't block validation.
        console.warn('failed to load concept registry', err)
      })
  }, [])

  const conceptById = useMemo(() => registry?.concepts ?? {}, [registry])

  const validate = useCallback(async (schema: Record<string, unknown>, gate?: ValidationGate) => {
    setServerError(null)
    setValidating(true)
    try {
      const res = await schemasApi.validate(schema, gate)
      setFindings(res.findings)
    } catch (err) {
      setServerError(errorMessage(err, 'request failed'))
      setFindings(null)
    } finally {
      setValidating(false)
    }
  }, [])

  const reset = useCallback(() => {
    setFindings(null)
    setServerError(null)
  }, [])

  const findingsBySeverity = useMemo(() => bucketFindings(findings), [findings])

  return {
    registry,
    conceptById,
    findings,
    findingsBySeverity,
    serverError,
    validating,
    validate,
    reset,
  }
}

/**
 * Re-run the concept checks on every schema a save produced, so the warnings
 * (antipatterns) the save gate lets through show without a click. Errors are
 * already refused by the gate itself (ADR-0001).
 */
export function useValidateSavedSchema(
  saved: Schema | null,
  validate: UseSchemaValidationResult['validate']
): void {
  useEffect(() => {
    if (saved) void validate(saved as unknown as Record<string, unknown>)
  }, [saved, validate])
}
